import { Users } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { Panel } from '../parts';
import { DEST_OPTIONS, MAX_BODY, MAX_TITLE, inputClass, type DestKind } from './constants';
import { Field } from './Field';
import type { BroadcastForm } from './useBroadcastForm';

type AdminLeague = { leagueId: string; name: string; memberCount: number };
type AdminMiniLeague = { miniLeagueId: string; name: string; leagueName: string; memberCount: number };

export function BroadcastComposePanels({
  form,
  leagues,
  miniLeagues
}: {
  form: BroadcastForm;
  leagues: AdminLeague[];
  miniLeagues: AdminMiniLeague[];
}) {
  const {
    title,
    body,
    ctaLabel,
    destKind,
    destLeagueId,
    destMiniId,
    customPath,
    audKind,
    audLeagueId,
    audMiniId,
    path,
    pathInvalid,
    audNeedsPick,
    estSize,
    touch,
    setTitle,
    setBody,
    setCtaLabel,
    setDestKind,
    setDestLeagueId,
    setDestMiniId,
    setCustomPath,
    setAudKind,
    setAudLeagueId,
    setAudMiniId
  } = form;

  return (
    <>
      <Panel title="Message" bodyClassName="p-4 md:p-5">
        <div className="grid gap-4">
          <Field label="Title" hint={`${title.length}/${MAX_TITLE}`}>
            <input
              className={inputClass}
              maxLength={MAX_TITLE}
              placeholder="Mini leagues are here"
              value={title}
              onChange={(e) => touch(setTitle)(e.target.value)}
            />
          </Field>
          <Field label="Message" hint={`${body.length}/${MAX_BODY}`}>
            <textarea
              className={cn(inputClass, 'min-h-[88px] resize-y')}
              maxLength={MAX_BODY}
              placeholder="Start a private league and go head-to-head with your friends every round."
              value={body}
              onChange={(e) => touch(setBody)(e.target.value)}
            />
          </Field>
        </div>
      </Panel>

      <Panel title="When tapped" bodyClassName="p-4 md:p-5">
        <div className="grid gap-4">
          <Field label="Open this screen" hint="Where a tap lands in the app.">
            <select
              className={inputClass}
              value={destKind}
              onChange={(e) => touch(setDestKind)(e.target.value as DestKind)}
            >
              {DEST_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>

          {destKind === 'league' && (
            <Field label="Which league">
              <select
                className={inputClass}
                value={destLeagueId}
                onChange={(e) => touch(setDestLeagueId)(e.target.value)}
              >
                <option value="">Choose a league…</option>
                {leagues.map((l) => (
                  <option key={l.leagueId} value={l.leagueId}>
                    {l.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {destKind === 'miniLeague' && (
            <Field label="Which mini league">
              <select
                className={inputClass}
                value={destMiniId}
                onChange={(e) => touch(setDestMiniId)(e.target.value)}
              >
                <option value="">Choose a mini league…</option>
                {miniLeagues.map((m) => (
                  <option key={m.miniLeagueId} value={m.miniLeagueId}>
                    {m.name} · {m.leagueName}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {destKind === 'custom' && (
            <Field label="Custom path" hint="Must start with “/”, e.g. /play/superliga-2025/standings">
              <input
                className={inputClass}
                placeholder="/leagues"
                value={customPath}
                onChange={(e) => touch(setCustomPath)(e.target.value)}
              />
            </Field>
          )}

          {destKind !== 'none' && (
            <Field label="Button label" hint="Optional CTA on the in-app card. Defaults to “See more”.">
              <input
                className={inputClass}
                placeholder="Try it now"
                value={ctaLabel}
                onChange={(e) => touch(setCtaLabel)(e.target.value)}
              />
            </Field>
          )}

          {path && (
            <div className="rounded-xl bg-wash px-3 py-2 text-[12px] text-ink2">
              Lands on <span className="num font-semibold text-ink">{path}</span>
            </div>
          )}
          {pathInvalid && (
            <p className="text-[12px] font-semibold text-danger">Path must start with “/”.</p>
          )}
        </div>
      </Panel>

      <Panel title="Audience" bodyClassName="p-4 md:p-5">
        <div className="grid gap-4">
          <Field label="Send to">
            <select
              className={inputClass}
              value={audKind}
              onChange={(e) => touch(setAudKind)(e.target.value as typeof audKind)}
            >
              <option value="all">All players</option>
              <option value="league">Members of a sport league</option>
              <option value="miniLeague">Members of a mini league</option>
            </select>
          </Field>

          {audKind === 'league' && (
            <Field label="Which league">
              <select
                className={inputClass}
                value={audLeagueId}
                onChange={(e) => touch(setAudLeagueId)(e.target.value)}
              >
                <option value="">Choose a league…</option>
                {leagues.map((l) => (
                  <option key={l.leagueId} value={l.leagueId}>
                    {l.name} · {l.memberCount} members
                  </option>
                ))}
              </select>
            </Field>
          )}

          {audKind === 'miniLeague' && (
            <Field label="Which mini league">
              <select
                className={inputClass}
                value={audMiniId}
                onChange={(e) => touch(setAudMiniId)(e.target.value)}
              >
                <option value="">Choose a mini league…</option>
                {miniLeagues.map((m) => (
                  <option key={m.miniLeagueId} value={m.miniLeagueId}>
                    {m.name} · {m.memberCount} members
                  </option>
                ))}
              </select>
            </Field>
          )}

          {!audNeedsPick && (
            <div className="flex items-center gap-2 text-[13px] text-ink2">
              <Users size={15} />
              <span>
                ~<span className="num font-semibold text-ink">{estSize}</span> player
                {estSize === 1 ? '' : 's'} before opt-outs
              </span>
            </div>
          )}
        </div>
      </Panel>
    </>
  );
}
